import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { DetailTreeNav, type TreeNode } from '@/components/layout/DetailTreeNav';
import { EmployeeTimesheetTab } from './EmployeeTimesheetTab';
import { EmployeeHomeJobSetter } from '@/components/employees/EmployeeHomeJobSetter';
import { PortalViewButton } from '@/components/portal/PortalViewButton';
import { PortalUsagePanel } from '@/components/portal/PortalUsagePanel';
import { getEmployees, getEmployeeById, createEmployee, updateEmployee, getEmployeeSchedule, getEmployeePaystubs, getEmployeeDocuments, getEmployeeTimeOff, createEmployeeTimeOff, updateEmployeeTimeOff, deleteEmployeeTimeOff, getEmployeeSkills, createEmployeeSkill, updateEmployeeSkill, deleteEmployeeSkill, getEmployeeJobAssignments, assignEmployeeToJob, unassignEmployeeFromJob, type EmployeeJobAssignment, getEmployeePay, addEmployeePayout, deleteEmployeePayout, type PaySummary, type EmployeePayout } from '@/lib/api/employees';
import { getJobs } from '@/lib/api/jobs';
import { WorkerAssignPanel } from '@/components/scheduling/WorkerAssignPanel';
import type { Employee, EmployeeScheduleEntry, EmployeePaystub, EmployeeDocument, EmployeeTimeOff, EmployeeSkill } from '@/lib/api/employees';
import { EmployeesTable } from '@/components/employees/EmployeesTable';
import { getCrews } from '@/lib/api/crews';
import type { Crew } from '@/lib/api/crews';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer, LargeTitleHeader, SegmentedControl, PullToRefresh, SwipeableRow, DetailSection } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { createInvite } from '@/lib/api/invites';
import { employeePath } from '@/lib/utils/navPaths';

// The old left-panel SwipeableRow list is superseded by the jobs-style main-area
// EmployeesTable; kept compiled-but-disabled for reference/rollback.
const LEGACY_LIST_PANEL: boolean = false;

export const EmployeesPage = () => {
  // ?new=1 auto-open (dashboard quick actions)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('new') === '1') {
      handleOpenModal();
      sp.delete('new');
      window.history.replaceState(null, '', window.location.pathname + (sp.toString() ? '?' + sp.toString() : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();
  const { id: urlEmployeeId, section: urlSection } = useParams<{ id?: string; section?: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [crews, setCrews] = useState<Crew[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  // Tab/section is URL-driven (/employees/:id/:section) — deep-linkable + Back traverses tabs.
  const EMPLOYEE_TABS = ['profile', 'jobs', 'schedule', 'timeoff', 'timesheets', 'pay', 'skills', 'paystubs', 'documents', 'usage'] as const;
  type EmployeeTab = (typeof EMPLOYEE_TABS)[number];
  const activeTab: EmployeeTab = EMPLOYEE_TABS.includes(urlSection as EmployeeTab)
    ? (urlSection as EmployeeTab)
    : 'profile';
  // When backing out to "All employees", suppress auto-selecting the first one.
  const [forceList, setForceList] = useState(false);
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState<'all' | 'employee' | 'subcontractor'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [crewFilter, setCrewFilter] = useState('');
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showCrewDropdown, setShowCrewDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [empSchedule, setEmpSchedule] = useState<EmployeeScheduleEntry[]>([]);
  const [empJobAssignments, setEmpJobAssignments] = useState<EmployeeJobAssignment[]>([]);
  const [empPay, setEmpPay] = useState<{ summary: PaySummary; payouts: EmployeePayout[] } | null>(null);
  const [payoutForm, setPayoutForm] = useState({ amount: '', paid_on: new Date().toISOString().slice(0, 10), method: 'etransfer', notes: '' });
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [assignableJobs, setAssignableJobs] = useState<{ id: number; title: string; status: string }[]>([]);
  const [assignJobId, setAssignJobId] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [empPaystubs, setEmpPaystubs] = useState<EmployeePaystub[]>([]);
  const [empDocuments, setEmpDocuments] = useState<EmployeeDocument[]>([]);
  const [empTimeOff, setEmpTimeOff] = useState<EmployeeTimeOff[]>([]);
  const [empSkills, setEmpSkills] = useState<EmployeeSkill[]>([]);
  const [loadingTab, setLoadingTab] = useState(false);

  const [showAddTimeOff, setShowAddTimeOff] = useState(false);
  const [timeOffForm, setTimeOffForm] = useState({ type: 'vacation' as EmployeeTimeOff['type'], start_date: '', end_date: '', notes: '' });
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [skillForm, setSkillForm] = useState({ skill_name: '', category: 'skill' as EmployeeSkill['category'], issue_date: '', expiry_date: '', notes: '' });
  const [editingSkillId, setEditingSkillId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    crew: '',
    status: 'active' as 'active' | 'inactive',
    hire_date: '',
    pay_rate: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    employee_type: 'employee' as 'employee' | 'subcontractor',
    company_name: '',
    wcb_clearance_number: '',
    wcb_clearance_expiry: '',
    liability_insurance_expiry: '',
  });
  const { toasts, removeToast, toast } = useToast();
  const [sendingInvite, setSendingInvite] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const empNameRef = useRef<HTMLInputElement>(null);
  const empSearchRef = useRef<HTMLInputElement>(null);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(key);
      setTimeout(() => setCopiedField(null), 1500);
    }).catch(() => {});
  };

  // Export employees list as CSV (#13)
  const exportEmployeesCsv = () => {
    const rows = [
      ['Name', 'Email', 'Phone', 'Role', 'Crew', 'Status', 'Type', 'Hire Date', 'Pay Rate'],
      ...filteredEmployees.map(e => [
        e.name ?? '',
        e.email ?? '',
        e.phone ?? '',
        e.role ?? '',
        e.crew ?? '',
        e.status ?? '',
        e.employee_type ?? 'employee',
        e.hire_date ? e.hire_date.split('T')[0] : '',
        e.pay_rate?.toString() ?? '',
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `employees-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success('Employee list exported');
  };
  const [confirmDeleteTimeOffId, setConfirmDeleteTimeOffId] = useState<number | null>(null);
  const [paystubSortCol, setPaystubSortCol] = useState<'pay_date' | 'gross_pay' | 'net_pay'>('pay_date');
  const [paystubSortDir, setPaystubSortDir] = useState<'asc' | 'desc'>('desc');

  const handlePaystubSort = (col: 'pay_date' | 'gross_pay' | 'net_pay') => {
    if (paystubSortCol === col) {
      setPaystubSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setPaystubSortCol(col);
      setPaystubSortDir('desc');
    }
  };
  const [confirmDeleteSkillId, setConfirmDeleteSkillId] = useState<number | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmSendInvite, setConfirmSendInvite] = useState(false);

  // Keyboard shortcuts: "n" = new employee, "/" = focus search, Esc = close modal (#11)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable;
    if (e.key === 'Escape') {
      if (isModalOpen) handleCloseModal();
      return;
    }
    if (isInput) return;
    if (e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      handleOpenModal();
    } else if (e.key === '/') {
      e.preventDefault();
      empSearchRef.current?.focus();
    }
  }, [isModalOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Autofocus first form field when modal opens (#11)
  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => empNameRef.current?.focus(), 50);
    }
  }, [isModalOpen]);

  useEffect(() => {
    loadEmployees();
    loadCrews();
  }, []);

  useEffect(() => {
    if (urlEmployeeId) {
      loadEmployeeDetails(urlEmployeeId);
    } else {
      // /employees with no id = the full list view (no auto-jump to first employee)
      setSelectedEmployee(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlEmployeeId]);

  const loadEmployeeDetails = async (employeeId: string) => {
    try {
      setEmpSchedule([]);
      setEmpJobAssignments([]);
      setEmpPay(null);
      setEmpPaystubs([]);
      setEmpDocuments([]);
      setEmpTimeOff([]);
      setEmpSkills([]);
      const data = await getEmployeeById(employeeId);
      setSelectedEmployee(data);
      if (!urlEmployeeId) {
        navigate(`/employees/${employeeId}`, { replace: true });
      }
    } catch (error) {
      console.error('Failed to load employee details:', error);
      toast.error('Failed to load employee details');
    }
  };

  const loadTabData = async (tab: string, employeeId: string) => {
    if (tab === 'pay') {
      setLoadingTab(true);
      try { setEmpPay(await getEmployeePay(employeeId)); } catch { /* empty state */ } finally { setLoadingTab(false); }
    } else if (tab === 'jobs') {
      setLoadingTab(true);
      try {
        setEmpJobAssignments(await getEmployeeJobAssignments(employeeId));
        if (assignableJobs.length === 0) {
          const r = await getJobs({ limit: 200 });
          setAssignableJobs(r.jobs.map(j => ({ id: j.id, title: j.title, status: j.status })));
        }
      } catch { /* section shows empty state */ } finally { setLoadingTab(false); }
    } else if (tab === 'schedule' && empSchedule.length === 0) {
      setLoadingTab(true);
      try { setEmpSchedule(await getEmployeeSchedule(employeeId)); } catch {} finally { setLoadingTab(false); }
    } else if (tab === 'paystubs' && empPaystubs.length === 0) {
      setLoadingTab(true);
      try { setEmpPaystubs(await getEmployeePaystubs(employeeId)); } catch {} finally { setLoadingTab(false); }
    } else if (tab === 'documents' && empDocuments.length === 0) {
      setLoadingTab(true);
      try { setEmpDocuments(await getEmployeeDocuments(employeeId)); } catch {} finally { setLoadingTab(false); }
    } else if (tab === 'timeoff' && empTimeOff.length === 0) {
      setLoadingTab(true);
      try { setEmpTimeOff(await getEmployeeTimeOff(employeeId)); } catch {} finally { setLoadingTab(false); }
    } else if (tab === 'skills' && empSkills.length === 0) {
      setLoadingTab(true);
      try { setEmpSkills(await getEmployeeSkills(employeeId)); } catch {} finally { setLoadingTab(false); }
    }
  };

  const handleAddTimeOff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    try {
      const created = await createEmployeeTimeOff(selectedEmployee.id, timeOffForm);
      setEmpTimeOff(prev => [created, ...prev]);
      setShowAddTimeOff(false);
      setTimeOffForm({ type: 'vacation', start_date: '', end_date: '', notes: '' });
      toast.success('Time-off request submitted');
    } catch {
      toast.error('Failed to submit time-off request');
    }
  };

  const handleTimeOffStatus = async (toid: number, status: 'approved' | 'denied') => {
    if (!selectedEmployee) return;
    try {
      const updated = await updateEmployeeTimeOff(selectedEmployee.id, toid, { status });
      setEmpTimeOff(prev => prev.map(t => t.id === toid ? updated : t));
      toast.success(`Request ${status}`);
    } catch {
      toast.error('Failed to update request');
    }
  };

  const handleDeleteTimeOff = (toid: number) => {
    if (!selectedEmployee) return;
    setConfirmDeleteTimeOffId(toid);
  };

  const confirmDeleteTimeOffAction = async () => {
    if (!selectedEmployee || confirmDeleteTimeOffId === null) return;
    const toid = confirmDeleteTimeOffId;
    setConfirmDeleteTimeOffId(null);
    try {
      await deleteEmployeeTimeOff(selectedEmployee.id, toid);
      setEmpTimeOff(prev => prev.filter(t => t.id !== toid));
      toast.success('Request deleted');
    } catch {
      toast.error('Failed to delete request');
    }
  };

  const handleAddSkill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    try {
      if (editingSkillId !== null) {
        const updated = await updateEmployeeSkill(selectedEmployee.id, editingSkillId, skillForm);
        setEmpSkills(prev => prev.map(s => s.id === editingSkillId ? updated : s));
        toast.success('Skill updated');
      } else {
        const created = await createEmployeeSkill(selectedEmployee.id, skillForm);
        setEmpSkills(prev => [...prev, created]);
        toast.success('Skill added');
      }
      setShowAddSkill(false);
      setEditingSkillId(null);
      setSkillForm({ skill_name: '', category: 'skill', issue_date: '', expiry_date: '', notes: '' });
    } catch {
      toast.error('Failed to save skill');
    }
  };

  const handleEditSkill = (skill: EmployeeSkill) => {
    setSkillForm({
      skill_name: skill.skill_name,
      category: skill.category,
      issue_date: skill.issue_date?.split('T')[0] || '',
      expiry_date: skill.expiry_date?.split('T')[0] || '',
      notes: skill.notes || '',
    });
    setEditingSkillId(skill.id);
    setShowAddSkill(true);
  };

  const handleDeleteSkill = (skillId: number) => {
    if (!selectedEmployee) return;
    setConfirmDeleteSkillId(skillId);
  };

  const confirmDeleteSkillAction = async () => {
    if (!selectedEmployee || confirmDeleteSkillId === null) return;
    const skillId = confirmDeleteSkillId;
    setConfirmDeleteSkillId(null);
    try {
      await deleteEmployeeSkill(selectedEmployee.id, skillId);
      setEmpSkills(prev => prev.filter(s => s.id !== skillId));
      toast.success('Skill deleted');
    } catch {
      toast.error('Failed to delete skill');
    }
  };

  const handleTabChange = (tab: typeof activeTab) => {
    if (urlEmployeeId) navigate(employeePath(urlEmployeeId, tab === 'profile' ? undefined : tab));
  };

  // URL drives the tab — load its data on tab click, deep link, and Back/Forward alike.
  useEffect(() => {
    if (selectedEmployee && activeTab !== 'profile') loadTabData(activeTab, selectedEmployee.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedEmployee?.id]);

  const handleEmployeeClick = (employeeId: string) => {
    setForceList(false);
    loadEmployeeDetails(employeeId);
    navigate(employeePath(employeeId));
  };

  const employeeTreeNodes: TreeNode[] = [
    { key: 'profile',   label: 'Profile',              icon: 'badge' },
    { key: 'jobs',      label: 'Jobs',                 icon: 'construction' },
    { key: 'schedule',  label: 'Schedule',             icon: 'calendar_month' },
    { key: 'timesheets',label: 'Timesheets',           icon: 'schedule' },
    { key: 'pay',       label: 'Pay',                  icon: 'payments' },
    { key: 'timeoff',   label: 'Time Off',             icon: 'beach_access' },
    { key: 'skills',    label: 'Skills & Performance', icon: 'workspace_premium' },
    { key: 'paystubs',  label: 'Paystubs',             icon: 'payments' },
    { key: 'documents', label: 'Documents',            icon: 'article' },
    { key: 'usage',     label: 'Portal Usage',         icon: 'monitoring' },
  ];

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setLoadingError('');
      const data = await getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Failed to load employees:', error);
      setLoadingError('Failed to load employees. Please try again.');
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const loadCrews = async () => {
    try {
      const data = await getCrews();
      setCrews(data);
    } catch (error) {
      console.error('Failed to load crews:', error);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const data = await getEmployees(searchQuery);
      setEmployees(data);
    } catch (error) {
      console.error('Failed to search employees:', error);
      toast.error('Failed to search employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery) {
        handleSearch();
      } else {
        loadEmployees();
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleOpenModal = (employee?: Employee) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        name: employee.name,
        email: employee.email,
        phone: employee.phone,
        role: employee.role,
        crew: employee.crew || '',
        status: employee.status,
        hire_date: (employee.hire_date || '').split('T')[0],
        pay_rate: employee.pay_rate?.toString() || '',
        emergency_contact_name: employee.emergency_contact_name || '',
        emergency_contact_phone: employee.emergency_contact_phone || '',
        employee_type: employee.employee_type || 'employee',
        company_name: employee.company_name || '',
        wcb_clearance_number: employee.wcb_clearance_number || '',
        wcb_clearance_expiry: (employee.wcb_clearance_expiry || '').split('T')[0],
        liability_insurance_expiry: (employee.liability_insurance_expiry || '').split('T')[0],
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
        crew: '',
        status: 'active',
        hire_date: '',
        pay_rate: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        employee_type: 'employee',
        company_name: '',
        wcb_clearance_number: '',
        wcb_clearance_expiry: '',
        liability_insurance_expiry: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEmployee(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const employeePayload = { ...formData, pay_rate: formData.pay_rate ? parseFloat(formData.pay_rate) : undefined };
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, employeePayload);
        toast.success('Employee updated successfully');
      } else {
        await createEmployee(employeePayload);
        toast.success('Employee created successfully');
      }
      handleCloseModal();
      loadEmployees();
      if (selectedEmployee && editingEmployee?.id === selectedEmployee.id) {
        loadEmployeeDetails(selectedEmployee.id);
      }
    } catch (error) {
      console.error('Failed to save employee:', error);
      toast.error('Failed to save employee');
    }
  };

  const handleEditCurrent = () => {
    if (selectedEmployee) {
      handleOpenModal(selectedEmployee);
    }
  };

  const handleDeactivate = () => {
    if (!selectedEmployee) return;
    setConfirmDeactivate(true);
  };

  const confirmDeactivateAction = async () => {
    if (!selectedEmployee) return;
    setConfirmDeactivate(false);
    try {
      await updateEmployee(selectedEmployee.id, {
        status: selectedEmployee.status === 'active' ? 'inactive' : 'active'
      });
      toast.success(`Employee ${selectedEmployee.status === 'active' ? 'deactivated' : 'activated'} successfully`);
      loadEmployees();
      loadEmployeeDetails(selectedEmployee.id);
    } catch (error) {
      console.error('Failed to update employee status:', error);
      toast.error('Failed to update employee status');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSendPortalInvite = () => {
    if (!selectedEmployee) return;
    if (!selectedEmployee.email) { toast.error('Employee has no email address'); return; }
    setConfirmSendInvite(true);
  };

  const confirmSendInviteAction = async () => {
    if (!selectedEmployee) return;
    setConfirmSendInvite(false);
    setSendingInvite(true);
    try {
      const invite = await createInvite({
        invite_type: 'employee',
        email: selectedEmployee.email,
        expires_days: 7,
      });
      const inviteUrl = invite.inviteUrl || `${window.location.origin}/portal/register?code=${invite.code}`;
      await navigator.clipboard.writeText(inviteUrl).catch(() => {});
      toast.success(`Invite sent to ${selectedEmployee.email}. Link copied to clipboard.`);
    } catch {
      toast.error('Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const uniqueRoles = Array.from(new Set(employees.map(e => e.role).filter(Boolean))).sort();
  const uniqueCrews = Array.from(new Set(employees.map(e => e.crew).filter(Boolean))).sort() as string[];

  const filteredEmployees = employees.filter(e => {
    if (employeeTypeFilter !== 'all' && (e.employee_type || 'employee') !== employeeTypeFilter) return false;
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (roleFilter && e.role !== roleFilter) return false;
    if (crewFilter && e.crew !== crewFilter) return false;
    return true;
  });

  // Show the section tree once an employee is open; the list is reachable via "All employees".
  const showTree = !!(selectedEmployee && urlEmployeeId && !forceList);

  return (
    <MainLayout
      clientListPanel={
        showTree ? (
          <DetailTreeNav
            title={selectedEmployee!.name}
            subtitle={selectedEmployee!.role || undefined}
            back={{ label: 'All employees', onClick: () => { setForceList(true); setSelectedEmployee(null); navigate('/employees'); } }}
            nodes={employeeTreeNodes}
            activeKey={activeTab}
            onSelect={(key) => handleTabChange(key as typeof activeTab)}
          />
        ) : (LEGACY_LIST_PANEL && (
        <div className="flex flex-col h-full bg-white">
          <div className="p-4 border-b border-gray-200">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Employees</h2>
              <p className="text-gray-500 text-sm mt-1">Manage employee details and schedules.</p>
            </div>
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => handleOpenModal()}
                className="flex flex-1 min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] hover:bg-primary/90 transition-colors"
                title="Add employee (N)"
              >
                <span className="material-symbols-outlined text-base">add</span>
                <span className="truncate">Add Employee</span>
              </button>
              {/* Export CSV button (#13) */}
              <button
                onClick={exportEmployeesCsv}
                className="flex items-center justify-center h-10 w-10 flex-shrink-0 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                title="Export CSV"
              >
                <span className="material-symbols-outlined text-base">download</span>
              </button>
            </div>
            <div className="relative mb-4">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">search</span>
              <input
                ref={empSearchRef}
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border-gray-300 bg-background-light focus:border-primary/50 h-10 placeholder:text-gray-400 pl-10 text-sm font-normal"
                placeholder="Search employees… (/)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setSearchQuery(''); (e.target as HTMLInputElement).blur(); }}}
              />
            </div>
            <div className="flex flex-wrap gap-2 relative">
              {/* Role filter */}
              <div className="relative">
                <button
                  onClick={() => { setShowRoleDropdown(v => !v); setShowCrewDropdown(false); setShowStatusDropdown(false); }}
                  className={`flex h-8 shrink-0 items-center justify-center gap-x-1.5 rounded-lg px-3 transition-colors text-sm font-medium ${roleFilter ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                >
                  <span className="material-symbols-outlined text-current" style={{ fontSize: '16px' }}>work</span>
                  {roleFilter || 'Role'}
                  <span className="material-symbols-outlined text-current" style={{ fontSize: '16px' }}>arrow_drop_down</span>
                </button>
                {showRoleDropdown && (
                  <div className="absolute top-9 left-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[160px] py-1">
                    <button onClick={() => { setRoleFilter(''); setShowRoleDropdown(false); }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 text-gray-500">All Roles</button>
                    {uniqueRoles.map(r => (
                      <button key={r} onClick={() => { setRoleFilter(r); setShowRoleDropdown(false); }} className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${roleFilter === r ? 'text-primary font-medium' : 'text-gray-700'}`}>{r}</button>
                    ))}
                  </div>
                )}
              </div>
              {/* Crew filter */}
              {uniqueCrews.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => { setShowCrewDropdown(v => !v); setShowRoleDropdown(false); setShowStatusDropdown(false); }}
                    className={`flex h-8 shrink-0 items-center justify-center gap-x-1.5 rounded-lg px-3 transition-colors text-sm font-medium ${crewFilter ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                  >
                    <span className="material-symbols-outlined text-current" style={{ fontSize: '16px' }}>groups</span>
                    {crewFilter || 'Crew'}
                    <span className="material-symbols-outlined text-current" style={{ fontSize: '16px' }}>arrow_drop_down</span>
                  </button>
                  {showCrewDropdown && (
                    <div className="absolute top-9 left-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[160px] py-1">
                      <button onClick={() => { setCrewFilter(''); setShowCrewDropdown(false); }} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 text-gray-500">All Crews</button>
                      {uniqueCrews.map(c => (
                        <button key={c} onClick={() => { setCrewFilter(c); setShowCrewDropdown(false); }} className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${crewFilter === c ? 'text-primary font-medium' : 'text-gray-700'}`}>{c}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Status filter */}
              <div className="relative">
                <button
                  onClick={() => { setShowStatusDropdown(v => !v); setShowRoleDropdown(false); setShowCrewDropdown(false); }}
                  className={`flex h-8 shrink-0 items-center justify-center gap-x-1.5 rounded-lg px-3 transition-colors text-sm font-medium ${statusFilter !== 'all' ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                >
                  <span className="material-symbols-outlined text-current" style={{ fontSize: '16px' }}>toggle_on</span>
                  {statusFilter === 'all' ? 'Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                  <span className="material-symbols-outlined text-current" style={{ fontSize: '16px' }}>arrow_drop_down</span>
                </button>
                {showStatusDropdown && (
                  <div className="absolute top-9 left-0 z-20 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[130px] py-1">
                    {(['all', 'active', 'inactive'] as const).map(s => (
                      <button key={s} onClick={() => { setStatusFilter(s); setShowStatusDropdown(false); }} className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${statusFilter === s ? 'text-primary font-medium' : 'text-gray-700'}`}>
                        {s === 'all' ? 'All Status' : s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Clear filters */}
              {(roleFilter || crewFilter || statusFilter !== 'all') && (
                <button onClick={() => { setRoleFilter(''); setCrewFilter(''); setStatusFilter('all'); }} className="flex h-8 items-center gap-1 text-xs text-gray-500 hover:text-red-500 px-2">
                  <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>close</span>
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto flex flex-col min-h-0">
            <div className="px-4 pt-3 pb-1">
              <SegmentedControl
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'employee', label: 'Staff' },
                  { value: 'subcontractor', label: 'Subs' },
                ]}
                value={employeeTypeFilter}
                onChange={setEmployeeTypeFilter}
                size="sm"
                fullWidth={true}
              />
            </div>
            <PullToRefresh onRefresh={loadEmployees} className="flex-1 min-h-0">
            {loading ? (
              <div className="p-3 space-y-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex items-center gap-3 p-2">
                    <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 bg-gray-100 rounded animate-pulse w-2/3" />
                      <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : loadingError ? (
              <div className="p-4 text-center">
                <p className="text-sm text-red-600 mb-2">{loadingError}</p>
                <button onClick={loadEmployees} className="text-xs text-primary hover:underline">Retry</button>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="p-6 text-center">
                <span className="material-symbols-outlined text-gray-300 text-4xl block mb-2">person_search</span>
                <p className="text-sm text-gray-500">{employees.length === 0 ? 'No employees yet' : 'No employees found'}</p>
                {(roleFilter || crewFilter || statusFilter !== 'all' || searchQuery) && (
                  <button onClick={() => { setRoleFilter(''); setCrewFilter(''); setStatusFilter('all'); setSearchQuery(''); }} className="mt-2 text-xs text-primary hover:underline">Clear filters</button>
                )}
                {/* Empty-state CTA (#22) */}
                {employees.length === 0 && !(roleFilter || crewFilter || statusFilter !== 'all' || searchQuery) && (
                  <button
                    onClick={() => handleOpenModal()}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-base">person_add</span>
                    Add First Employee
                  </button>
                )}
              </div>
            ) : (
              filteredEmployees.map((employee) => (
                <SwipeableRow
                  key={employee.id}
                  actions={[
                    { key: 'edit', label: 'Edit', icon: 'edit', variant: 'default', onClick: () => handleOpenModal(employee) },
                    {
                      key: 'status',
                      label: employee.status === 'active' ? 'Deactivate' : 'Activate',
                      icon: employee.status === 'active' ? 'no_accounts' : 'check_circle',
                      variant: employee.status === 'active' ? 'danger' : 'neutral',
                      onClick: async () => {
                        try {
                          await import('@/lib/api/employees').then(m => m.updateEmployee(employee.id, { status: employee.status === 'active' ? 'inactive' : 'active' }));
                          loadEmployees();
                        } catch {}
                      },
                    },
                  ]}
                >
                  <div
                    onClick={() => handleEmployeeClick(employee.id)}
                    className={`flex items-center gap-4 p-3 cursor-pointer transition-colors ${
                      selectedEmployee?.id === employee.id
                        ? 'bg-primary/10 border-l-2 border-l-primary'
                        : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                    }`}
                  >
                    {employee.profile_image ? (
                      <div
                        className="bg-center bg-no-repeat aspect-square bg-cover rounded-full size-12 flex-shrink-0"
                        style={{ backgroundImage: `url(${employee.profile_image})` }}
                      />
                    ) : (
                      <div className="bg-primary/20 text-primary rounded-full size-12 flex items-center justify-center font-bold flex-shrink-0">
                        {getInitials(employee.name)}
                      </div>
                    )}
                    <div className="flex flex-col justify-center flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className={`text-sm leading-normal line-clamp-1 font-semibold ${
                          selectedEmployee?.id === employee.id ? 'text-primary' : 'text-gray-900'
                        }`}>
                          {employee.name}
                        </p>
                        {employee.employee_type === 'subcontractor' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 shrink-0">
                            Sub
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="text-xs text-gray-500 truncate">{employee.role}</p>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${employee.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                      </div>
                    </div>
                  </div>
                </SwipeableRow>
              ))
            )}
            </PullToRefresh>
          </div>
        </div>
        ))
      }
      noPadding
    >
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="flex h-full w-full overflow-hidden">
        <div className="flex-1 flex flex-col overflow-y-auto bg-background-light">
          {selectedEmployee ? (
            <>
              <div className="bg-white px-6 pt-0 pb-2">
                <LargeTitleHeader
                  title={selectedEmployee.name}
                  subtitle={selectedEmployee.role + (selectedEmployee.crew ? ` · ${selectedEmployee.crew}` : '')}
                  actions={
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        onClick={handleEditCurrent}
                        className="flex items-center justify-center gap-1.5 rounded-lg h-9 px-3 bg-gray-200 text-gray-800 text-sm font-bold hover:bg-gray-300 transition-colors"
                      >
                        <span className="material-symbols-outlined text-base">edit</span>
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        onClick={handleSendPortalInvite}
                        disabled={sendingInvite}
                        title="Send portal login invite to this employee"
                        className="flex items-center justify-center gap-1.5 rounded-lg h-9 px-3 bg-primary/10 text-primary text-sm font-bold hover:bg-primary/20 transition-colors disabled:opacity-50"
                      >
                        <span className="material-symbols-outlined text-base">send</span>
                        <span className="hidden sm:inline">{sendingInvite ? 'Sending…' : 'Invite'}</span>
                      </button>
                      <PortalViewButton type="employee" recordId={Number(selectedEmployee.id)} onError={msg => toast.error(msg)} />
                      <button
                        onClick={handleDeactivate}
                        className={`flex items-center justify-center gap-1.5 rounded-lg h-9 px-3 text-sm font-bold transition-colors ${
                          selectedEmployee.status === 'active'
                            ? 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                            : 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                        }`}
                      >
                        <span className="material-symbols-outlined text-base">
                          {selectedEmployee.status === 'active' ? 'no_accounts' : 'check_circle'}
                        </span>
                        <span className="hidden sm:inline">{selectedEmployee.status === 'active' ? 'Deactivate' : 'Activate'}</span>
                      </button>
                    </div>
                  }
                />
              </div>
              <div className="flex-grow bg-white px-1.5 py-3 md:p-6 flex flex-col gap-3">

                <DetailSection title="Profile" icon="badge" sectionKey="profile" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <>
                  {/* Task-suggestion clearance — gates the "+ Suggest a task" button in the portal */}
                  <div className="mt-3 flex items-center justify-between gap-3 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">Can suggest planner tasks</p>
                      <p className="text-[11px] text-gray-400">Their suggestions wait for your approval before the crew sees them.</p>
                    </div>
                    <button
                      onClick={async () => {
                        const next = !selectedEmployee.can_suggest_tasks;
                        setSelectedEmployee(prev => prev ? { ...prev, can_suggest_tasks: next } : prev);
                        try { await updateEmployee(selectedEmployee.id, { can_suggest_tasks: next } as Partial<Employee>); }
                        catch { setSelectedEmployee(prev => prev ? { ...prev, can_suggest_tasks: !next } : prev); }
                      }}
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${selectedEmployee.can_suggest_tasks ? 'bg-[#C77D3F]' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${selectedEmployee.can_suggest_tasks ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                  <div className="pt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <div className="flex flex-col group">
                      <p className="text-sm font-medium text-gray-500">Email Address</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        {selectedEmployee.email ? (
                          <>
                            {/* click-to-email (#12) */}
                            <a href={`mailto:${selectedEmployee.email}`} className="text-base text-primary hover:underline">{selectedEmployee.email}</a>
                            <button onClick={() => copyToClipboard(selectedEmployee.email, 'emp_email')} title="Copy" className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary p-0.5 rounded">
                              <span className="material-symbols-outlined text-xs">{copiedField === 'emp_email' ? 'check' : 'content_copy'}</span>
                            </button>
                          </>
                        ) : <span className="text-base text-gray-400">N/A</span>}
                      </div>
                    </div>
                    <div className="flex flex-col group">
                      <p className="text-sm font-medium text-gray-500">Phone Number</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        {selectedEmployee.phone ? (
                          <>
                            {/* click-to-call (#12) */}
                            <a href={`tel:${selectedEmployee.phone}`} className="text-base text-primary hover:underline">{selectedEmployee.phone}</a>
                            <button onClick={() => copyToClipboard(selectedEmployee.phone, 'emp_phone')} title="Copy" className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary p-0.5 rounded">
                              <span className="material-symbols-outlined text-xs">{copiedField === 'emp_phone' ? 'check' : 'content_copy'}</span>
                            </button>
                          </>
                        ) : <span className="text-base text-gray-400">N/A</span>}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-gray-500">Hire Date</p>
                      <p className="text-base text-gray-900 mt-1">{formatDate(selectedEmployee.hire_date)}</p>
                    </div>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-gray-500">Pay Rate</p>
                      <p className="text-base text-gray-900 mt-1">
                        {selectedEmployee.pay_rate ? `$${Number(selectedEmployee.pay_rate).toFixed(2)}/hr` : 'Not set'}
                      </p>
                    </div>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-gray-500">Employment Status</p>
                      <span className={`mt-2 inline-flex items-center justify-center rounded-full px-2.5 py-0.5 w-fit ${
                        selectedEmployee.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        <p className="whitespace-nowrap text-sm capitalize">{selectedEmployee.status}</p>
                      </span>
                    </div>
                    {selectedEmployee.emergency_contact_name && (
                      <div className="flex flex-col">
                        <p className="text-sm font-medium text-gray-500">Emergency Contact</p>
                        <p className="text-base text-gray-900 mt-1">{selectedEmployee.emergency_contact_name}</p>
                        {selectedEmployee.emergency_contact_phone && (
                          <p className="text-sm text-gray-500">{selectedEmployee.emergency_contact_phone}</p>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedEmployee.employee_type === 'subcontractor' && (
                    <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                      <h4 className="font-semibold text-purple-900 text-sm flex items-center gap-2">
                        <span className="material-symbols-outlined text-sm">business</span>
                        Subcontractor Details
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {selectedEmployee.company_name && (
                          <div>
                            <p className="text-xs text-purple-600 font-medium">Company</p>
                            <p className="text-gray-900">{selectedEmployee.company_name}</p>
                          </div>
                        )}
                        {selectedEmployee.wcb_clearance_number && (
                          <div>
                            <p className="text-xs text-purple-600 font-medium">WCB #</p>
                            <p className="text-gray-900">{selectedEmployee.wcb_clearance_number}</p>
                          </div>
                        )}
                        {selectedEmployee.wcb_clearance_expiry && (
                          <div>
                            <p className="text-xs text-purple-600 font-medium">WCB Expiry</p>
                            <p className={`font-medium ${
                              new Date(selectedEmployee.wcb_clearance_expiry) < new Date(Date.now() + 30 * 86400000)
                                ? 'text-red-600' : 'text-gray-900'
                            }`}>
                              {new Date(selectedEmployee.wcb_clearance_expiry).toLocaleDateString('en-CA')}
                            </p>
                          </div>
                        )}
                        {selectedEmployee.liability_insurance_expiry && (
                          <div>
                            <p className="text-xs text-purple-600 font-medium">Liability Insurance Expiry</p>
                            <p className={`font-medium ${
                              new Date(selectedEmployee.liability_insurance_expiry) < new Date(Date.now() + 30 * 86400000)
                                ? 'text-red-600' : 'text-gray-900'
                            }`}>
                              {new Date(selectedEmployee.liability_insurance_expiry).toLocaleDateString('en-CA')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  </>
                </DetailSection>

                <DetailSection title="Jobs" icon="construction" sectionKey="jobs" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <div className="pt-4 space-y-4">
                    <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5 text-xs text-blue-800">
                      <span className="material-symbols-outlined text-base">info</span>
                      Jobs assigned here control what {selectedEmployee.name.split(' ')[0]} sees in their portal:
                      the job, its schedule, and its worker-visible documents.
                    </div>

                    {/* Assign to a job */}
                    <div className="flex flex-wrap items-end gap-2 border border-gray-200 rounded-xl p-3 bg-gray-50/60">
                      <label className="flex flex-col gap-1 flex-1 min-w-[14rem]">
                        <span className="text-xs font-medium text-gray-500">Assign to job</span>
                        <select
                          value={assignJobId}
                          onChange={e => setAssignJobId(e.target.value)}
                          className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white"
                        >
                          <option value="">Choose a job…</option>
                          {assignableJobs
                            .filter(j => !empJobAssignments.some(a => a.job_id === j.id))
                            .map(j => <option key={j.id} value={j.id}>{j.title} ({j.status})</option>)}
                        </select>
                      </label>
                      <button
                        disabled={!assignJobId || assignBusy}
                        onClick={async () => {
                          if (!assignJobId) return;
                          setAssignBusy(true);
                          try {
                            await assignEmployeeToJob(selectedEmployee.id, Number(assignJobId));
                            setAssignJobId('');
                            setEmpJobAssignments(await getEmployeeJobAssignments(selectedEmployee.id));
                            toast.success('Assigned — their portal now shows this job.');
                          } catch { toast.error('Failed to assign'); }
                          finally { setAssignBusy(false); }
                        }}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                      >
                        <span className={`material-symbols-outlined text-base ${assignBusy ? 'animate-spin' : ''}`}>{assignBusy ? 'progress_activity' : 'add'}</span>
                        Assign
                      </button>
                    </div>

                    {/* Current assignments */}
                    {loadingTab ? (
                      <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
                    ) : empJobAssignments.length === 0 ? (
                      <p className="text-gray-500 text-sm">Not assigned to any jobs yet — their portal will be empty until you assign one.</p>
                    ) : (
                      <div className="space-y-2">
                        {empJobAssignments.map(a => (
                          <div key={a.id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-3">
                            <div className="bg-primary/10 rounded-lg p-2 flex-shrink-0">
                              <span className="material-symbols-outlined text-primary text-base">construction</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900 truncate">{a.job_title}</p>
                              <p className="text-xs text-gray-400">
                                {a.job_number ? `${a.job_number} · ` : ''}assigned {new Date(a.assigned_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full capitalize bg-gray-100 text-gray-600 flex-shrink-0">{a.job_status?.replace('_', ' ')}</span>
                            <button
                              onClick={async () => {
                                try {
                                  await unassignEmployeeFromJob(selectedEmployee.id, a.job_id);
                                  setEmpJobAssignments(prev => prev.filter(x => x.id !== a.id));
                                  toast.success('Removed from job');
                                } catch { toast.error('Failed to remove'); }
                              }}
                              className="text-gray-400 hover:text-red-600 flex-shrink-0"
                              title="Remove from this job"
                            >
                              <span className="material-symbols-outlined text-base">close</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DetailSection>

                <DetailSection title="Pay" icon="payments" sectionKey="pay" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <div className="pt-4 space-y-4">
                    {loadingTab ? (
                      <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
                    ) : !empPay ? (
                      <p className="text-gray-500 text-sm">Couldn't load pay data.</p>
                    ) : (
                      <>
                        {empPay.summary.hourly_rate <= 0 && (
                          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800">
                            <span className="material-symbols-outlined text-base">warning</span>
                            No hourly rate set for {selectedEmployee.name} — earned/owed will read $0. Set it in Profile → Edit.
                          </div>
                        )}
                        {/* Summary tiles */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                          <div className="bg-white border border-gray-200 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500">Approved hours</p>
                            <p className="text-xl font-bold text-gray-900 mt-0.5">{empPay.summary.approved_hours.toFixed(1)}h</p>
                            <p className="text-[11px] text-gray-400">@ ${empPay.summary.hourly_rate.toFixed(2)}/h{empPay.summary.pending_hours > 0 ? ` · ${empPay.summary.pending_hours.toFixed(1)}h pending` : ''}</p>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500">Earned</p>
                            <p className="text-xl font-bold text-gray-900 mt-0.5">${empPay.summary.earned.toFixed(2)}</p>
                          </div>
                          <div className="bg-white border border-gray-200 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500">Paid out</p>
                            <p className="text-xl font-bold text-gray-900 mt-0.5">${empPay.summary.paid.toFixed(2)}</p>
                          </div>
                          <div className={`border rounded-xl p-3.5 ${empPay.summary.owed > 0.005 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                            <p className={`text-xs ${empPay.summary.owed > 0.005 ? 'text-amber-700' : 'text-green-700'}`}>Owed</p>
                            <p className={`text-xl font-bold mt-0.5 ${empPay.summary.owed > 0.005 ? 'text-amber-800' : 'text-green-800'}`}>${empPay.summary.owed.toFixed(2)}</p>
                          </div>
                        </div>

                        {/* Record a payment */}
                        <div className="border border-gray-200 rounded-xl p-3 bg-gray-50/60">
                          <h4 className="text-sm font-bold text-gray-900 mb-2">Record a payment</h4>
                          <div className="flex flex-wrap items-end gap-2">
                            <label className="flex flex-col gap-1">
                              <span className="text-xs font-medium text-gray-500">Amount</span>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                <input type="number" min="0.01" step="0.01" value={payoutForm.amount}
                                  onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                                  className="h-9 pl-6 pr-2 rounded-lg border border-gray-300 text-sm w-28 bg-white" />
                              </div>
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-xs font-medium text-gray-500">Date paid</span>
                              <input type="date" value={payoutForm.paid_on}
                                onChange={e => setPayoutForm(f => ({ ...f, paid_on: e.target.value }))}
                                className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white" />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-xs font-medium text-gray-500">Method</span>
                              <select value={payoutForm.method}
                                onChange={e => setPayoutForm(f => ({ ...f, method: e.target.value }))}
                                className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white">
                                <option value="etransfer">e-Transfer</option>
                                <option value="cheque">Cheque</option>
                                <option value="cash">Cash</option>
                                <option value="payroll">Payroll</option>
                                <option value="other">Other</option>
                              </select>
                            </label>
                            <label className="flex flex-col gap-1 flex-1 min-w-[10rem]">
                              <span className="text-xs font-medium text-gray-500">Note (optional)</span>
                              <input type="text" value={payoutForm.notes} placeholder="e.g. week of Jul 14"
                                onChange={e => setPayoutForm(f => ({ ...f, notes: e.target.value }))}
                                className="h-9 px-2 rounded-lg border border-gray-300 text-sm bg-white" />
                            </label>
                            <button
                              disabled={payoutBusy || !payoutForm.amount || Number(payoutForm.amount) <= 0}
                              onClick={async () => {
                                setPayoutBusy(true);
                                try {
                                  await addEmployeePayout(selectedEmployee.id, {
                                    amount: Number(payoutForm.amount),
                                    paid_on: payoutForm.paid_on,
                                    method: payoutForm.method,
                                    notes: payoutForm.notes.trim() || undefined,
                                  });
                                  setPayoutForm(f => ({ ...f, amount: '', notes: '' }));
                                  setEmpPay(await getEmployeePay(selectedEmployee.id));
                                  toast.success('Payment recorded');
                                } catch { toast.error('Failed to record payment'); }
                                finally { setPayoutBusy(false); }
                              }}
                              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                            >
                              <span className={`material-symbols-outlined text-base ${payoutBusy ? 'animate-spin' : ''}`}>{payoutBusy ? 'progress_activity' : 'payments'}</span>
                              Record
                            </button>
                          </div>
                          {empPay.summary.owed > 0.005 && (
                            <button
                              onClick={() => setPayoutForm(f => ({ ...f, amount: empPay.summary.owed.toFixed(2) }))}
                              className="mt-2 text-xs text-primary font-medium hover:underline"
                            >
                              Pay full owed amount (${empPay.summary.owed.toFixed(2)})
                            </button>
                          )}
                        </div>

                        {/* Payout history */}
                        {empPay.payouts.length === 0 ? (
                          <p className="text-gray-500 text-sm">No payments recorded yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {empPay.payouts.map(p => (
                              <div key={p.id} className="flex items-center gap-3 border border-gray-100 rounded-xl p-3">
                                <div className="bg-green-50 rounded-lg p-2 flex-shrink-0">
                                  <span className="material-symbols-outlined text-green-600 text-base">paid</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-sm text-gray-900">${Number(p.amount).toFixed(2)}</p>
                                  <p className="text-xs text-gray-400">
                                    {new Date(String(p.paid_on).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    {' · '}{p.method}{p.notes ? ` · ${p.notes}` : ''}{p.created_by_name ? ` · by ${p.created_by_name}` : ''}
                                  </p>
                                </div>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Delete this $${Number(p.amount).toFixed(2)} payment record?`)) return;
                                    try {
                                      await deleteEmployeePayout(selectedEmployee.id, p.id);
                                      setEmpPay(await getEmployeePay(selectedEmployee.id));
                                      toast.success('Payment record deleted');
                                    } catch { toast.error('Failed to delete'); }
                                  }}
                                  className="text-gray-400 hover:text-red-600 flex-shrink-0"
                                  title="Delete payment record"
                                >
                                  <span className="material-symbols-outlined text-base">delete</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </DetailSection>

                <DetailSection title="Schedule" icon="calendar_month" sectionKey="schedule" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <div className="pt-4 space-y-3">
                    {selectedEmployee.user_id && <EmployeeHomeJobSetter userId={Number(selectedEmployee.user_id)} />}
                    {loadingTab ? (
                      <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
                    ) : empSchedule.length === 0 ? (
                      <p className="text-gray-500 text-sm">No upcoming schedule found. Assign this employee to a schedule event from a job or the calendar.</p>
                    ) : (
                      empSchedule.map(s => (
                        <div key={s.id} className="border border-gray-100 rounded-xl p-3 space-y-2">
                          <div className="flex items-start gap-3">
                            <div className="bg-primary/10 rounded-lg p-2 flex-shrink-0">
                              <span className="material-symbols-outlined text-primary text-base">calendar_month</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-gray-900">{s.job_title || 'Unnamed Job'}</p>
                              <p className="text-xs text-gray-500">{new Date(String(s.scheduled_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })}{s.end_date && s.end_date !== s.scheduled_date ? ` – ${new Date(String(s.end_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}</p>
                              {s.site_address && <p className="text-xs text-gray-400 truncate">{s.site_address}</p>}
                              {s.crew_name && <p className="text-xs text-gray-400">{s.crew_name}</p>}
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize flex-shrink-0 ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : s.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{s.status.replace('_', ' ')}</span>
                          </div>
                          {/* Workers panel for this event */}
                          <details className="ml-11">
                            <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-800 flex items-center gap-1 select-none">
                              <span className="material-symbols-outlined text-xs">manage_accounts</span>
                              Manage individual workers on this event
                            </summary>
                            <div className="mt-2">
                              <WorkerAssignPanel scheduleId={s.id} scheduledDate={s.scheduled_date} />
                            </div>
                          </details>
                        </div>
                      ))
                    )}
                  </div>
                </DetailSection>

                <DetailSection title="Time Off" icon="beach_access" sectionKey="timeoff" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <div className="pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-gray-700">Time-Off Requests</h4>
                      <button onClick={() => { setShowAddTimeOff(v => !v); }} className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80">
                        <span className="material-symbols-outlined text-base">add</span>New Request
                      </button>
                    </div>

                    {showAddTimeOff && (
                      <form onSubmit={handleAddTimeOff} className="mb-4 border border-primary/20 rounded-xl p-4 bg-primary/5 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                            <select value={timeOffForm.type} onChange={e => setTimeOffForm(f => ({ ...f, type: e.target.value as EmployeeTimeOff['type'] }))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                              {(['vacation','sick','personal','unpaid','other'] as const).map(t => (
                                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                            <input type="date" required value={timeOffForm.start_date} onChange={e => setTimeOffForm(f => ({ ...f, start_date: e.target.value }))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                            <input type="date" required value={timeOffForm.end_date} min={timeOffForm.start_date} onChange={e => setTimeOffForm(f => ({ ...f, end_date: e.target.value }))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                            <input type="text" value={timeOffForm.notes} onChange={e => setTimeOffForm(f => ({ ...f, notes: e.target.value }))}
                              placeholder="Optional" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button type="button" onClick={() => setShowAddTimeOff(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">Cancel</button>
                          <button type="submit" className="text-sm font-medium bg-primary text-white rounded-lg px-4 py-1.5 hover:bg-primary/90">Submit</button>
                        </div>
                      </form>
                    )}

                    {loadingTab ? (
                      <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
                    ) : empTimeOff.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-8">No time-off requests on record.</p>
                    ) : (
                      <div className="space-y-2">
                        {empTimeOff.map(t => {
                          const start = new Date(String(t.start_date).slice(0, 10) + 'T12:00:00');
                          const end = new Date(String(t.end_date).slice(0, 10) + 'T12:00:00');
                          const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
                          const statusColor = t.status === 'approved' ? 'bg-green-100 text-green-700' : t.status === 'denied' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700';
                          const typeColor: Record<EmployeeTimeOff['type'], string> = { vacation: 'bg-blue-50 text-blue-600', sick: 'bg-orange-50 text-orange-600', personal: 'bg-purple-50 text-purple-600', unpaid: 'bg-gray-50 text-gray-600', other: 'bg-gray-50 text-gray-600' };
                          return (
                            <div key={t.id} className="border border-gray-100 rounded-lg p-3 flex items-start gap-3 hover:bg-gray-50">
                              <div className={`rounded-lg px-2 py-1 text-xs font-semibold capitalize ${typeColor[t.type]}`}>{t.type}</div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                  {start.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} – {end.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
                                  <span className="text-gray-400 font-normal ml-1">({days} day{days !== 1 ? 's' : ''})</span>
                                </p>
                                {t.notes && <p className="text-xs text-gray-500 mt-0.5">{t.notes}</p>}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-medium ${statusColor}`}>{t.status}</span>
                                {t.status === 'pending' && (
                                  <>
                                    <button onClick={() => handleTimeOffStatus(t.id, 'approved')} title="Approve" className="text-green-600 hover:text-green-800">
                                      <span className="material-symbols-outlined text-base">check_circle</span>
                                    </button>
                                    <button onClick={() => handleTimeOffStatus(t.id, 'denied')} title="Deny" className="text-red-500 hover:text-red-700">
                                      <span className="material-symbols-outlined text-base">cancel</span>
                                    </button>
                                  </>
                                )}
                                <button onClick={() => handleDeleteTimeOff(t.id)} className="text-gray-300 hover:text-red-400">
                                  <span className="material-symbols-outlined text-base">delete</span>
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </DetailSection>

                <DetailSection title="Skills & Performance" icon="workspace_premium" sectionKey="skills" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <div className="pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-semibold text-gray-700">Skills & Certifications</h4>
                      <button onClick={() => { setEditingSkillId(null); setSkillForm({ skill_name: '', category: 'skill', issue_date: '', expiry_date: '', notes: '' }); setShowAddSkill(v => !v); }}
                        className="flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80">
                        <span className="material-symbols-outlined text-base">add</span>Add Skill
                      </button>
                    </div>

                    {showAddSkill && (
                      <form onSubmit={handleAddSkill} className="mb-4 border border-primary/20 rounded-xl p-4 bg-primary/5 space-y-3">
                        <p className="text-xs font-semibold text-gray-700">{editingSkillId !== null ? 'Edit Skill' : 'New Skill / Certification'}</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                            <input type="text" required value={skillForm.skill_name} onChange={e => setSkillForm(f => ({ ...f, skill_name: e.target.value }))}
                              placeholder="e.g. First Aid, Forklift Operator, WHMIS" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                            <select value={skillForm.category} onChange={e => setSkillForm(f => ({ ...f, category: e.target.value as EmployeeSkill['category'] }))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                              {(['certification','ticket','license','skill','other'] as const).map(c => (
                                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Issue Date</label>
                            <input type="date" value={skillForm.issue_date} onChange={e => setSkillForm(f => ({ ...f, issue_date: e.target.value }))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date</label>
                            <input type="date" value={skillForm.expiry_date} onChange={e => setSkillForm(f => ({ ...f, expiry_date: e.target.value }))}
                              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                            <input type="text" value={skillForm.notes} onChange={e => setSkillForm(f => ({ ...f, notes: e.target.value }))}
                              placeholder="Optional" className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white" />
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button type="button" onClick={() => { setShowAddSkill(false); setEditingSkillId(null); }} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">Cancel</button>
                          <button type="submit" className="text-sm font-medium bg-primary text-white rounded-lg px-4 py-1.5 hover:bg-primary/90">
                            {editingSkillId !== null ? 'Update' : 'Add'}
                          </button>
                        </div>
                      </form>
                    )}

                    {loadingTab ? (
                      <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
                    ) : empSkills.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-8">No skills or certifications on record.</p>
                    ) : (
                      <div className="space-y-2">
                        {(['certification','ticket','license','skill','other'] as const).map(cat => {
                          const catSkills = empSkills.filter(s => s.category === cat);
                          if (catSkills.length === 0) return null;
                          const catLabel: Record<EmployeeSkill['category'], string> = { certification: 'Certifications', ticket: 'Tickets', license: 'Licenses', skill: 'Skills', other: 'Other' };
                          const catColor: Record<EmployeeSkill['category'], string> = { certification: 'bg-blue-50 border-blue-200', ticket: 'bg-amber-50 border-amber-200', license: 'bg-purple-50 border-purple-200', skill: 'bg-gray-50 border-gray-200', other: 'bg-gray-50 border-gray-200' };
                          return (
                            <div key={cat}>
                              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1.5">{catLabel[cat]}</p>
                              {catSkills.map(s => {
                                const now = Date.now();
                                const exp = s.expiry_date ? new Date(s.expiry_date).getTime() : null;
                                const expiringSoon = exp !== null && exp < now + 30 * 86400000;
                                const expired = exp !== null && exp < now;
                                return (
                                  <div key={s.id} className={`border rounded-lg px-3 py-2.5 mb-1.5 flex items-start gap-2 ${catColor[cat]} ${expired ? 'border-red-300 bg-red-50' : expiringSoon ? 'border-yellow-300 bg-yellow-50' : ''}`}>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-gray-900">{s.skill_name}</p>
                                        {expired && <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Expired</span>}
                                        {!expired && expiringSoon && <span className="text-xs px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">Expiring soon</span>}
                                      </div>
                                      <div className="flex gap-3 mt-0.5 text-xs text-gray-500">
                                        {s.issue_date && <span>Issued {new Date(s.issue_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })}</span>}
                                        {s.expiry_date && <span className={expired ? 'text-red-500 font-medium' : expiringSoon ? 'text-yellow-600 font-medium' : ''}>Expires {new Date(s.expiry_date).toLocaleDateString('en-CA', { month: 'short', year: 'numeric' })}</span>}
                                        {s.notes && <span>{s.notes}</span>}
                                      </div>
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                      <button onClick={() => handleEditSkill(s)} className="text-gray-400 hover:text-primary">
                                        <span className="material-symbols-outlined text-base">edit</span>
                                      </button>
                                      <button onClick={() => handleDeleteSkill(s.id)} className="text-gray-300 hover:text-red-400">
                                        <span className="material-symbols-outlined text-base">delete</span>
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </DetailSection>

                <DetailSection title="Paystubs" icon="payments" sectionKey="paystubs" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <div className="pt-4">
                    {loadingTab ? (
                      <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
                    ) : empPaystubs.length === 0 ? (
                      <p className="text-gray-500 text-sm">No payroll records found.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                              {/* Sortable paystub columns (#20) */}
                              {(['pay_date', 'gross_pay', 'net_pay'] as const).map(col => {
                                const labels: Record<string, string> = { pay_date: 'Pay Date', gross_pay: 'Gross', net_pay: 'Net Pay' };
                                const isRight = col !== 'pay_date';
                                return (
                                  <th key={col} className={`pb-2 font-medium cursor-pointer select-none hover:text-gray-800 ${isRight ? 'text-right' : ''}`} onClick={() => handlePaystubSort(col)}>
                                    <span className="inline-flex items-center gap-0.5">{labels[col]}{paystubSortCol === col ? <span className="material-symbols-outlined text-xs">{paystubSortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span> : <span className="material-symbols-outlined text-xs opacity-20">unfold_more</span>}</span>
                                  </th>
                                );
                              })}
                              <th className="pb-2 font-medium">Period</th>
                              <th className="pb-2 font-medium text-right">Deductions</th>
                              <th className="pb-2 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...empPaystubs].sort((a, b) => {
                              const aVal = paystubSortCol === 'pay_date' ? new Date(a.pay_date).getTime() : Number(a[paystubSortCol]);
                              const bVal = paystubSortCol === 'pay_date' ? new Date(b.pay_date).getTime() : Number(b[paystubSortCol]);
                              return paystubSortDir === 'asc' ? aVal - bVal : bVal - aVal;
                            }).map(p => (
                              <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                                <td className="py-2">{new Date(p.pay_date).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                <td className="py-2 text-right">${Number(p.gross_pay).toFixed(2)}</td>
                                <td className="py-2 text-right font-semibold text-green-700">${Number(p.net_pay).toFixed(2)}</td>
                                <td className="py-2 text-gray-500 text-xs">{new Date(String(p.pay_period_start).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} – {new Date(String(p.pay_period_end).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</td>
                                <td className="py-2 text-right text-red-600">-${Number(p.total_deductions).toFixed(2)}</td>
                                <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${p.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{p.status}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </DetailSection>

                <DetailSection title="Documents" icon="article" sectionKey="documents" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <div className="pt-4">
                    {loadingTab ? (
                      <div className="h-24 animate-pulse bg-gray-100 rounded-xl" />
                    ) : empDocuments.length === 0 ? (
                      <p className="text-gray-500 text-sm">No documents found for jobs assigned to this employee.</p>
                    ) : (
                      <div className="space-y-2">
                        {empDocuments.map(d => (
                          <div key={d.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                            <span className="material-symbols-outlined text-gray-400 text-xl">description</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{d.file_name || 'Document'}</p>
                              {d.job_title && <p className="text-xs text-gray-500">{d.job_title}</p>}
                              <p className="text-xs text-gray-400">{new Date(d.created_at).toLocaleDateString('en-CA')}</p>
                            </div>
                            {d.document_type && <span className="text-xs text-gray-500 capitalize">{d.document_type}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </DetailSection>

                <DetailSection title="Timesheets" icon="schedule" sectionKey="timesheets" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <EmployeeTimesheetTab employeeId={selectedEmployee.id} />
                </DetailSection>

                <DetailSection title="Portal Usage" icon="monitoring" sectionKey="usage" activeKey={activeTab} onActivate={k => handleTabChange(k as EmployeeTab)}>
                  <PortalUsagePanel kind="employee" id={Number(selectedEmployee.id)} />
                </DetailSection>
              </div>
            </>
          ) : (
            <div className="px-1.5 py-3 md:p-6">
              <EmployeesTable
                employees={filteredEmployees}
                loading={loading}
                onOpen={handleEmployeeClick}
                onEdit={emp => handleOpenModal(emp)}
                onExport={exportEmployeesCsv}
                onInvite={async emp => {
                  if (!emp.email) { toast.error(`${emp.name} has no email address — add one first.`); return; }
                  try {
                    const invite = await createInvite({
                      invite_type: 'employee',
                      email: emp.email,
                      target_employee_id: Number(emp.id),
                      is_reset: true, // new email registers; existing account gets a password reset
                      expires_days: 7,
                    });
                    const url = invite.inviteUrl || `${window.location.origin}/portal/register?code=${invite.code}`;
                    await navigator.clipboard.writeText(url).catch(() => {});
                    toast.success(`${emp.user_id ? 'Access-reset' : 'Invite'} link emailed to ${emp.email} — link also copied.`);
                  } catch {
                    toast.error('Failed to send invite');
                  }
                }}
                onToggleStatus={async emp => {
                  try {
                    await updateEmployee(emp.id, { status: emp.status === 'active' ? 'inactive' : 'active' });
                    loadEmployees();
                  } catch { /* ignore */ }
                }}
                onAdd={() => handleOpenModal()}
              />
            </div>
          )}
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                onClick={handleCloseModal}
              />

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="px-6 pt-5 pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">
                        {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                      </h3>
                      <button
                        type="button"
                        onClick={handleCloseModal}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <span className="material-symbols-outlined">close</span>
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Full Name *
                        </label>
                        <input
                          ref={empNameRef}
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Phone *
                        </label>
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Role *
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="e.g., Lead Carpenter, Apprentice"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Crew
                        </label>
                        <select
                          value={formData.crew}
                          onChange={(e) => setFormData({ ...formData, crew: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">No Crew</option>
                          {crews.map((crew) => (
                            <option key={crew.id} value={crew.name}>
                              {crew.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Hire Date *
                        </label>
                        <input
                          type="date"
                          required
                          value={formData.hire_date}
                          onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Pay Rate ($/hr)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.pay_rate}
                          onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Emergency Contact Name
                        </label>
                        <input
                          type="text"
                          value={formData.emergency_contact_name}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Emergency Contact Phone
                        </label>
                        <input
                          type="tel"
                          value={formData.emergency_contact_phone}
                          onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">
                          Status *
                        </label>
                        <select
                          required
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-1">Type</label>
                        <select
                          value={formData.employee_type}
                          onChange={e => setFormData({ ...formData, employee_type: e.target.value as 'employee' | 'subcontractor' })}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value="employee">Employee</option>
                          <option value="subcontractor">Subcontractor</option>
                        </select>
                      </div>
                      {formData.employee_type === 'subcontractor' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-1">Company Name</label>
                            <input
                              type="text"
                              value={formData.company_name}
                              onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              placeholder="ABC Framing Ltd"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-1">WCB Clearance #</label>
                            <input
                              type="text"
                              value={formData.wcb_clearance_number}
                              onChange={e => setFormData({ ...formData, wcb_clearance_number: e.target.value })}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-1">WCB Expiry</label>
                              <input
                                type="date"
                                value={formData.wcb_clearance_expiry}
                                onChange={e => setFormData({ ...formData, wcb_clearance_expiry: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-900 mb-1">Liability Insurance Expiry</label>
                              <input
                                type="date"
                                value={formData.liability_insurance_expiry}
                                onChange={e => setFormData({ ...formData, liability_insurance_expiry: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-4 py-2 text-sm font-medium text-gray-900 border border-gray-200 rounded-lg hover:bg-white"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90"
                    >
                      {editingEmployee ? 'Update Employee' : 'Create Employee'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {confirmDeleteTimeOffId !== null && (
        <ConfirmDialog
          title="Delete Time-Off Request"
          message="Delete this time-off request?"
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteTimeOffAction}
          onCancel={() => setConfirmDeleteTimeOffId(null)}
        />
      )}

      {confirmDeleteSkillId !== null && (
        <ConfirmDialog
          title="Delete Skill / Certification"
          message="Delete this skill/certification?"
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteSkillAction}
          onCancel={() => setConfirmDeleteSkillId(null)}
        />
      )}

      {confirmDeactivate && selectedEmployee && (
        <ConfirmDialog
          title={selectedEmployee.status === 'active' ? 'Deactivate Employee' : 'Activate Employee'}
          message={`Are you sure you want to ${selectedEmployee.status === 'active' ? 'deactivate' : 'activate'} this employee?`}
          confirmLabel={selectedEmployee.status === 'active' ? 'Deactivate' : 'Activate'}
          variant={selectedEmployee.status === 'active' ? 'danger' : 'default'}
          onConfirm={confirmDeactivateAction}
          onCancel={() => setConfirmDeactivate(false)}
        />
      )}

      {confirmSendInvite && selectedEmployee && (
        <ConfirmDialog
          title="Send Portal Invite"
          message={`Send portal invite to ${selectedEmployee.name} (${selectedEmployee.email})?`}
          confirmLabel="Send Invite"
          variant="default"
          onConfirm={confirmSendInviteAction}
          onCancel={() => setConfirmSendInvite(false)}
        />
      )}
    </MainLayout>
  );
};
